import {
  CalendarFilled,
  DeleteFilled,
  UserAddOutlined,
  UsergroupAddOutlined,
} from "@ant-design/icons";
import { Button, Checkbox, Empty, Input, Select } from "antd";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useCreateLedger } from "../../api/api";
import { useDeleteLedger } from "../../api/api.delete";
import { useGetAllLedgers } from "../../api/api.get";
import ConfirmDialog from "../../components/dialog/confirm.dialog";
import { useNotifications } from "../../components/notifications/notification";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { ILedger } from "../../types/types";
import { capitalize } from "../../utils/functions";
import { useGroupContext } from "../../providers/groups.provider";

export default function LedgersPage() {
  const navigate = useNavigate();
  const { defaultGroupId } = useGroupContext();
  const { mutateAsync } = useCreateLedger(defaultGroupId);

  const { data: ledgers, refetch: refetchTrips } =
    useGetAllLedgers(defaultGroupId);

  const { notification, notifyError, notifySuccess } = useNotifications();
  const { mutateAsync: deleteAsync } = useDeleteLedger(defaultGroupId);

  const handleDeleteLedger = () => {
    deleteAsync({ ledgerId: confirmDeleteLedger! })
      .then(() => {
        refetchTrips();
        setConfirmDeleteLedger(undefined);
        notifySuccess({
          title: "Ledger",
          description: "Deleted successfully.",
        });
      })
      .catch(() => notifyError("Please try again"));
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      currency: "",
      shared: false,
    },
    validationSchema: Yup.object({
      currency: Yup.string().required("Please select currency"),
      name: Yup.string().required("Please enter name"),
    }),
    onSubmit: async (values) => {
      mutateAsync({
        currency: values.currency,
        shared: values.shared,
        name: values.name,
      })
        .then(async () => {
          await refetchTrips();
          notifySuccess({ description: "Ledger successfully created" });
          formik.resetForm();
        })
        .catch(() => notifyError("Whoops! Please try again."));
    },
  });

  const [confirmDeleteLedger, setConfirmDeleteLedger] = useState<string>();
  return (
    <div className="min-h-screen bg-slate-50">
      {notification}

      {confirmDeleteLedger && (
        <ConfirmDialog
          onCancel={() => setConfirmDeleteLedger(undefined)}
          onOk={handleDeleteLedger}
          open={!!confirmDeleteLedger}
        />
      )}

      <main className="pt-8 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">Ledgers</h1>
            <p className="text-muted-foreground">
              Manage important transactions securely without accounting it to
              regular expenses
            </p>
          </div>

          {/* Two Panel Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Panel: Trips List */}

            <div className="lg:col-span-2">
              {!ledgers ||
                (ledgers.length === 0 && (
                  <Empty description="No Ledgers Found" className="mt-10" />
                ))}

              {ledgers && ledgers.length > 0 && (
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      My Ledgers
                    </CardTitle>
                    <CardDescription>
                      {ledgers?.length} ledger{ledgers?.length !== 1 ? "s" : ""}{" "}
                      recorded
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {ledgers.length === 0 ? (
                        <div className="text-center py-8">
                          <p className="text-muted-foreground">
                            No ledgers yet. Create your first!
                          </p>
                        </div>
                      ) : (
                        ledgers.map((ledger: ILedger) => {
                          return (
                            <div
                              key={ledger.id}
                              className="p-4 border border-slate-200 rounded-lg hover:shadow-md hover:border-primary/50 transition cursor-pointer bg-slate-50 hover:bg-slate-100"
                            >
                              <div className="flex justify-between items-start mb-3">
                                <div
                                  className="flex-1"
                                  onClick={() => {
                                    navigate(`/ledgers/${ledger.id}`);
                                  }}
                                >
                                  <h3 className="font-semibold text-foreground text-lg mb-1">
                                    {capitalize(ledger.name)}
                                  </h3>
                                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2 font-bold">
                                    {ledger.currency}
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    icon={<DeleteFilled />}
                                    type="text"
                                    onClick={() =>
                                      setConfirmDeleteLedger(ledger.id)
                                    }
                                  />
                                </div>
                              </div>

                              {/* Ledger Details */}
                              <div
                                className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-200"
                                onClick={() => {
                                  navigate(`/ledgers/${ledger.id}`);
                                }}
                              >
                                <div className="flex items-center gap-2">
                                  {ledger.shared && (
                                    <UsergroupAddOutlined className="w-4 h-4 text-primary" />
                                  )}

                                  {!ledger.shared && (
                                    <UserAddOutlined className="w-4 h-4 text-primary" />
                                  )}

                                  <div>
                                    {ledger.shared && (
                                      <p className="text-xs text-muted-foreground bg-orange-500 text-white rounded-xl px-2 py-1">
                                        Shared
                                      </p>
                                    )}
                                    {!ledger.shared && (
                                      <p className="text-xs text-muted-foreground text-blue-500">
                                        Only You
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <CalendarFilled className="w-4 h-4 text-primary" />
                                  <div className="text-xs text-muted-foreground">
                                    {dayjs(ledger.createdDate).format(
                                      "DD MMM YYYY",
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right Panel: Add Trip Form */}
            <div>
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle>Add New Ledger</CardTitle>
                  <CardDescription>
                    Create your next secured transaction list
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={formik.handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2">
                        Ledger Name *
                      </label>
                      <Input
                        type="text"
                        placeholder="e.g., Summer Vacation"
                        {...formik.getFieldProps("name")}
                        required
                        status={!!formik.errors.name ? "error" : undefined}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Give your ledger a name
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2">
                        Currency *
                      </label>
                      <Select
                        options={[
                          {
                            label: "USD",
                            value: "USD",
                          },
                          {
                            label: "CAD",
                            value: "CAD",
                          },
                        ]}
                        style={{ width: 120 }}
                        onChange={(value: string) =>
                          formik.setFieldValue("currency", value)
                        }
                        status={!!formik.errors.currency ? "error" : undefined}
                      />

                      <p className="text-xs text-muted-foreground mt-1">
                        Select currency
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2">
                        Shared *
                      </label>

                      <Checkbox
                        value={formik.values.shared}
                        onChange={(e) =>
                          formik.setFieldValue("shared", e.target.checked)
                        }
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Share with other members in group?
                      </p>
                    </div>

                    {/* Buttons */}
                    <div className="flex justify-between gap-2 pt-4">
                      <Button
                        className="flex-1"
                        onClick={formik.submitForm}
                        type="primary"
                        size="large"
                        disabled={Object.keys(formik.errors).length > 0}
                      >
                        Add Ledger
                      </Button>
                      <Button
                        className="flex-1"
                        onClick={() => formik.resetForm()}
                        type="text"
                        size="large"
                      >
                        Clear
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
